import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, DownloadIcon } from "lucide-react";
import styles from "./FrequencyBucketTable.module.css";
import TCUStyles from "../TCU.module.css";
import TablePagination from "./TablePagination";
import DepotVehiclesPopover, { DEPOT_VEHICLES_MODAL_ID } from "./DepotVehiclesPopover";
import { BUCKET_COLUMNS } from "./frequencyBuckets";
import { useDeviceHealthContextData } from "./context/Context";
import {
    downloadCsv,
    getDeviceHealthCityWiseTableData as getCityWise,
    getDeviceHealthDeviceHealthDepotWiseData as getDepotWise
} from "./DeviceHealthService";

const FIXED_COL_COUNT = 4;
const COL_COUNT = FIXED_COL_COUNT + BUCKET_COLUMNS.length;
const ROWS_PER_PAGE = 10; // fixed, not user-changeable

const TONE_CLASS = {
    yellow: styles.tone_yellow,
    green: styles.tone_green,
    orange: styles.tone_orange,
    red: styles.tone_red
};

// bootstrap 4 + 5 attributes so the shared CustomModal opens either way
const MODAL_TRIGGER = {
    "data-toggle": "modal",
    "data-bs-toggle": "modal",
    "data-target": `#${DEPOT_VEHICLES_MODAL_ID}`,
    "data-bs-target": `#${DEPOT_VEHICLES_MODAL_ID}`
};

/* ============================ HELPERS ============================ */
const displayValue = (val) =>
    val === null || val === undefined || val === "" ? "-" : val;

// deviceHealthDepotWise depot name field
const getDepotName = (row) =>
    row?.Depot ?? row?.depot ?? row?.Depot_Name ?? row?.depot_name ?? row?.depotName ?? "";

/* ============================ SMALL COMPONENTS ============================ */
function StatusBadge({ frequency }) {
    const isHealthy = Number(frequency) > 80;
    return (
        <span className={`${styles.statusBadge} ${isHealthy ? styles.statusHealthy : styles.statusUnhealthy}`}>
        </span>
    );
}

function ChartLoader() {
    return (
        <div className={styles.loadingState}>
            <span className={styles.spinner} />
            <span>Loading...</span>
        </div>
    );
}

/* ============================ MAIN ============================ */
export default function FrequencyBucketTable({
    title = "Device Bucketing",
    subtitle = "Vehicles per frequency & delay bucket — click a city to view its depots"
}) {
    const { GlobalFilter } = useDeviceHealthContextData();
    const [cityRows, setCityRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [openCity, setOpenCity] = useState(null);
    const [depotRows, setDepotRows] = useState([]);
    const [isDepotLoading, setIsDepotLoading] = useState(false);
    const [popoverSelection, setPopoverSelection] = useState(null);

    // API 1: city-wise rows
    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setCurrentPage(1);
        setOpenCity(null);

        getCityWise({ ...GlobalFilter })
            .then((res) => {
                if (!cancelled) setCityRows(res?.data || []);
            })
            .catch(() => {
                if (!cancelled) setCityRows([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [GlobalFilter]);

    // API 2: depot-wise rows for the expanded city
    useEffect(() => {
        if (!openCity) return;

        let cancelled = false;
        setIsDepotLoading(true);
        setDepotRows([]);

        getDepotWise({ ...GlobalFilter, city: [{ label: openCity, value: openCity }] })
            .then((res) => {
                if (!cancelled) setDepotRows(res?.data || []);
            })
            .catch(() => {
                if (!cancelled) setDepotRows([]);
            })
            .finally(() => {
                if (!cancelled) setIsDepotLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [GlobalFilter, openCity]);

    /* ---- row pagination (city rows) ---- */
    const totalItems = cityRows.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / ROWS_PER_PAGE));
    const safePage = Math.min(currentPage, totalPages);

    const paginatedRows = useMemo(() => {
        const start = (safePage - 1) * ROWS_PER_PAGE;
        return cityRows.slice(start, start + ROWS_PER_PAGE);
    }, [cityRows, safePage]);

    const toggleCity = (city) => {
        setOpenCity((prev) => (prev === city ? null : city));
    };

    const changePage = (page) => {
        setOpenCity(null);
        setCurrentPage(page);
    };

    // same CSV api as the Overall Frequency KPI, with the current global filters
    const downloadBucketData = () => {
        downloadCsv("downloadOverallFrequency", { ...GlobalFilter }, title).catch(() => { });
    };

    const openDepotPopover = (row) => {
        setPopoverSelection({ city: row.City ?? openCity, depot: getDepotName(row) });
    };

    /* ---------- cell renderers ---------- */
    const renderBucketCell = (row, bucket) => {
        // API rows use "0-5" … ">60"; avgDelay* is the fallback naming
        const value = row?.[bucket.field] ?? row?.[bucket.key];
        const toneClass = Number(value) ? TONE_CLASS[bucket.tone] : styles.tone_grey;

        return (
            <td key={bucket.key} className={`${styles.bucketCell} ${toneClass}`}>
                {displayValue(value)}
            </td>
        );
    };

    const renderDepotRow = (row, idx) => {
        const depot = getDepotName(row);

        return (
        // the whole row opens the vehicles popover, not just the depot name
        <tr
            key={`depot-${depot || idx}`}
            {...MODAL_TRIGGER}
            className={`${styles.depotRow} ${styles.depotRowClickable}`}
            title={`${depot} — click to view vehicles`}
            onClick={() => openDepotPopover(row)}
        >
            <td className={styles.depotCell}>
                <span className={styles.cityContent}>
                    <span className={styles.depotIndicator} />
                    <button
                        type="button"
                        className={styles.depotLink}
                    >
                        {displayValue(depot)}
                    </button>
                </span>
            </td>
            <td>{displayValue(row.Vehicle_Count)}</td>
            <td><StatusBadge frequency={row.frequency} /></td>
            <td>{displayValue(row.frequency)}</td>
            {BUCKET_COLUMNS.map((b) => renderBucketCell(row, b))}
        </tr>
        );
    };

    const renderDepotRows = () => {
        if (isDepotLoading) {
            return (
                <tr className={styles.depotRow}>
                    <td colSpan={COL_COUNT}>
                        <ChartLoader />
                    </td>
                </tr>
            );
        }

        if (!depotRows.length) {
            return (
                <tr className={styles.depotRow}>
                    <td colSpan={COL_COUNT}>
                        <div className={styles.loadingState}>No depots found</div>
                    </td>
                </tr>
            );
        }

        return depotRows.map(renderDepotRow);
    };

    const renderCityRow = (row) => {
        const isOpen = openCity === row.City;

        return (
            <React.Fragment key={row.City}>
                <tr
                    className={`${styles.cityRow} ${isOpen ? styles.cityRowActive : ""}`}
                    onClick={() => toggleCity(row.City)}
                >
                    <td className={styles.cityCell}>
                        <span className={styles.cityContent}>
                            <span className={styles.accordionIcon}>
                                {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                            </span>
                            <span
                                className={styles.cityName}
                                title={`${row.City} — click to ${isOpen ? "hide" : "view"} depots`}
                            >
                                {row.City}
                            </span>
                        </span>
                    </td>
                    <td>{displayValue(row.Vehicle_Count)}</td>
                    <td><StatusBadge frequency={row.frequency} /></td>
                    <td>{displayValue(row.frequency)}</td>
                    {BUCKET_COLUMNS.map((b) => renderBucketCell(row, b))}
                </tr>

                {isOpen && renderDepotRows()}
            </React.Fragment>
        );
    };

    return (
        <div className={`card ${styles.card} mx-3 mt-2 mb-2`}>
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                <div className={TCUStyles.titleWrap}>
                    <span className={TCUStyles.titleBar} />
                    <span className={TCUStyles.title}>{title}</span>
                    <span className={TCUStyles.subtitle}>{subtitle}</span>
                </div>

                <button
                    type="button"
                    className={`${styles.downloadBtn} custom-Btn-animation`}
                    title="Download bucketing data (CSV)"
                    onClick={downloadBucketData}
                >
                    <DownloadIcon size={15} />
                </button>
            </div>


            <div className={`table-responsive mx-2 mt-1 ${styles.tableWrapper}`}>
                <table className={`table table-bordered table-hover align-middle mb-0 ${styles.mainTable}`}>
                    <colgroup>
                        <col className={styles.colLocation} />
                        <col className={styles.colCount} />
                        <col className={styles.colStatus} />
                        <col className={styles.colFreq} />
                        {BUCKET_COLUMNS.map((b) => (
                            <col key={b.key} className={styles.colBucket} />
                        ))}
                    </colgroup>

                    <thead className="sticky-top">
                        <tr>
                            <th>City / Depot</th>
                            <th>VIN Count</th>
                            <th>Status</th>
                            <th>Freq%</th>
                            {BUCKET_COLUMNS.map((b) => (
                                <th key={b.key}>{b.label}</th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan={COL_COUNT}>
                                    <ChartLoader />
                                </td>
                            </tr>
                        ) : totalItems === 0 ? (
                            <tr>
                                <td colSpan={COL_COUNT}>
                                    <div className={styles.loadingState}>
                                        No cities found
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            paginatedRows.map(renderCityRow)
                        )}
                    </tbody>
                </table>
            </div>

            {!isLoading && (
                <TablePagination
                    page={safePage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    pageSize={ROWS_PER_PAGE}
                    onPageChange={changePage}
                    itemLabel="cities"
                />
            )}

            <DepotVehiclesPopover selection={popoverSelection} />
        </div>
    );
}
